import type {
    default as CalendarEvent,
    CalendarItemTypeTypeEx,
} from 'owa-calendar-types/lib/types/CalendarEvent';
import type CalendarItemExperienceType from 'owa-service/lib/contract/CalendarItemExperienceType';
import getDefaultReminder from './getDefaultReminder';
import initializeCalendarFolderId from './initializeCalendarFolderId';
import { addHours, addMinutes, now, startOfHour, startOfDay, addDays, OwaDate } from 'owa-datetime';
import type { ClientFolderId } from 'owa-client-ids';
import { getNoCharmInfoObject } from 'owa-calendar-charms-store';
import { getConfiguredEventStartTimeEndTime } from 'owa-calendar-cloud-settings-utils';
import getDefaultEventDuration from 'owa-session-store/lib/selectors/getDefaultEventDuration';
import type { OwaDateRange } from 'owa-datetime-utils/lib/OwaDateRange';

const DEFAULT_REMINDER_FALLBACK = 15;

/**
 * Initializes a full CalendarEvent when given a partial or null calendar event
 * @param data Optional partial calendar event
 * @param isInitializedEvent Optional - set to true for existing/initialized events (e.g. edit appointment in quick compose)
 * @param shouldRespectStartEndTime Indicates if the start time and end time should be respected (no speedy meetings applied)
 */
export default function initializeCalendarEvent(
    data?: Partial<CalendarEvent>,
    isInitializedEvent?: boolean,
    shouldRespectStartEndTime?: boolean
): CalendarEvent {
    // Create default dates
    let startDate: OwaDate;
    let endDate: OwaDate;
    let eventDuration: number = getDefaultEventDuration();
    let defaultReminderMinutes: number = DEFAULT_REMINDER_FALLBACK;
    let parentFolderId: ClientFolderId;
    let isOnlineMeeting: boolean = false;
    let shouldAdjustDateRange: boolean = !isInitializedEvent && !shouldRespectStartEndTime;

    if (!data) {
        // If we dont have data, we need to initialize the start and the end dates
        startDate = addHours(startOfHour(now()), 1);
        endDate = addMinutes(startDate, eventDuration);
        parentFolderId = initializeCalendarFolderId();
        defaultReminderMinutes = getDefaultReminder(false /* isAllDayEvent */);
    } else {
        if (data.IsAllDayEvent) {
            startDate = data.Start || startOfDay(now());
            endDate = data.End || startOfDay(addDays(startDate, 1));
            shouldAdjustDateRange = false;
        } else {
            startDate = data.Start || addHours(startOfHour(now()), 1);
            endDate = data.End || addMinutes(startDate, eventDuration);
        }
        defaultReminderMinutes =
            data.ReminderMinutesBeforeStart || getDefaultReminder(data.IsAllDayEvent);

        if (!data.ParentFolderId) {
            parentFolderId = initializeCalendarFolderId();
        }

        isOnlineMeeting = data.IsOnlineMeeting;
    }

    if (shouldAdjustDateRange) {
        // Adjust startDate and endDate based on the shorten event duration setting
        // Makes sure the adjustment is made only once - when the event is initialized for the first time
        const adjustedDateRange: OwaDateRange = getConfiguredEventStartTimeEndTime(
            startDate,
            endDate
        );
        startDate = adjustedDateRange.start;
        endDate = adjustedDateRange.end;
    }

    // Setup default event
    let event: CalendarEvent = {
        ItemId: null,
        ParentFolderId: parentFolderId,
        Subject: null,
        ReminderIsSet: defaultReminderMinutes != -1,
        ReminderMinutesBeforeStart: defaultReminderMinutes,
        LastModifiedTime: null,
        InstanceKey: null,
        Preview: null,
        __type: null,
        Start: startDate,
        StartTimeZoneId: startDate.tz,
        End: endDate,
        EndTimeZoneId: endDate.tz,
        IsAllDayEvent: false,
        IsRoomRequested: false,
        AutoRoomBookingStatus: undefined,
        IsDraft: false,
        FreeBusyType: 'Busy',
        IsMeeting: false,
        IsCancelled: false,
        IsRecurring: false,
        IsResponseRequested: true, // to be coherent with jsmvvm
        CalendarItemType: 'Single' as CalendarItemTypeTypeEx,
        ResponseType: null,
        Organizer: null,
        RequiredAttendees: null,
        OptionalAttendees: null,
        Resources: null,
        AppointmentReplyTime: null,
        Recurrence: null,
        Location: null,
        IntendedFreeBusyStatus: null,
        IsOrganizer: true,
        AppointmentReplyName: null,
        IsSeriesCancelled: null,
        CharmId: getNoCharmInfoObject().IconId,
        Locations: null,
        ClientSeriesId: null,
        EffectiveRights: null,
        HasAttachments: false,
        HasBlockedImages: false,
        Attachments: null,
        Sensitivity: 'Normal',
        Body: {
            Value: '',
            BodyType: 'HTML',
        }, // Initialize with empty HTML value since we want the body to be HTML bodytype so that attachments don't fail and online meeting blob gets properly HTML formatted
        EntityNamesMap: 0,
        TailoredXpEntities: null,
        UID: null,
        ExperienceType: 'Unspecified' as CalendarItemExperienceType,
        DoNotForwardMeeting: false,
        IsOnlineMeeting: isOnlineMeeting,
        JoinOnlineMeetingUrl: null,
        Categories: null,
        InboxReminders: null,
        MeetingRequestWasSent: null,
        ExtractionSourceId: null,
        OnlineMeetingProvider: null,
        OnlineMeetingJoinUrl: null,
        OnlineMeetingConferenceId: null,
        OnlineMeetingTollNumber: null,
        OnlineMeetingTollFreeNumbers: null,
        SeriesMasterItemId: null,
        ExtractionSourceInternetMessageId: null,
        PersonId: null,
        InReplyTo: null,
        HideAttendees: false,
        IsFreeBusyOnly: null,
        SkypeTeamsProperties: null,
        DocLinks: [],
        DateTimeCreated: null,
        AppendOnSend: [],
        IsBookedFreeBlocks: false,
        AssociatedTasks: [],
        CollabSpace: null,
        TravelTimeEventsLinked: false,
        FlexEventsMetadata: null,
    };

    // Override default with provided data
    if (data) {
        event = {
            ...event,
            ...data,
            ...{ Start: startDate, End: endDate },
        };

        if (!data.FreeBusyType) {
            event.FreeBusyType = data.IsAllDayEvent ? 'Free' : 'Busy';
        }
    }

    return event;
}
